import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

/**
 * Per-instance expansion state for feed items (headline details, firehose
 * story details).
 *
 * Why this exists: feed widgets render the same item objects as their
 * siblings — every instance of a widget type shares the React Query cache
 * entry / feed cache holding them. The item components used to persist
 * "details open" by mutating a field on the shared item object, which made
 * opening a headline in one widget open it in every other widget watching
 * the same feed. State that belongs to one instance must not live on data
 * that all instances share.
 *
 * A list wraps itself in `ExpandedItemsProvider`; items read through
 * `useExpandedItem`. Outside a provider (standalone views) the hook falls
 * back to plain local state.
 */
interface ExpandedItemsApi {
  isExpanded: (id: number) => boolean;
  toggle: (id: number) => void;
}

const ExpandedItemsContext = createContext<ExpandedItemsApi | null>(null);

export function ExpandedItemsProvider({ children }: { children: React.ReactNode }) {
  const [expandedIds, setExpandedIds] = useState<Set<number>>(() => new Set());

  const api = useMemo<ExpandedItemsApi>(
    () => ({
      isExpanded: (id) => expandedIds.has(id),
      toggle: (id) =>
        setExpandedIds((prev) => {
          const next = new Set(prev);
          if (next.has(id)) {
            next.delete(id);
          } else {
            next.add(id);
          }
          return next;
        }),
    }),
    [expandedIds]
  );

  return (
    <ExpandedItemsContext.Provider value={api}>
      {children}
    </ExpandedItemsContext.Provider>
  );
}

/**
 * Details open/closed for one item, plus a toggle. Survives virtualized list
 * recycling: with a provider the state is keyed by id (so the same item is
 * still open after being scrolled out and back); without one, local state is
 * reset when the component is recycled onto a different id.
 */
export function useExpandedItem(
  id: number,
  initial = false
): [boolean, () => void] {
  const api = useContext(ExpandedItemsContext);

  const [localExpanded, setLocalExpanded] = useState(initial);

  const expanded = api ? api.isExpanded(id) : localExpanded;

  const toggle = useCallback(() => {
    if (api) {
      api.toggle(id);
    } else {
      setLocalExpanded((prev) => !prev);
    }
  }, [api, id]);

  // No provider (standalone views): reset when recycled onto a different id.
  useEffect(() => {
    if (!api) setLocalExpanded(initial);
    // `initial` is intentionally not a dependency — only recycling matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, id]);

  return [expanded, toggle];
}
